import { Box, Icon, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import React from 'react'

import { LocalAction, LocalActionType } from '@/app/module/campaigns/types'
import { hasPreview } from '@/app/module/campaigns/utils/actions/helpers'
import { ActionOption } from '@/app/module/campaigns/utils/actions/options'
import { FilesType, PersonalizationType } from '@/app/types'

import { editableActions } from './add-to-library-button'
import FinalActionPreview from './final-action-preview'

type Props = {
  actionOptions: ActionOption[]
  continueLabel?: string
  files: FilesType
  finalAction?: LocalAction
  personalizationList: PersonalizationType
  onAdd: (type: LocalActionType) => void
  onEdit: (type: LocalActionType) => void
  onRemove: () => void
}

const FinalActionsRow: React.FC<Props> = ({
  actionOptions,
  continueLabel = 'Go to Next Part',
  files,
  finalAction,
  personalizationList,
  onAdd,
  onEdit,
  onRemove,
}) => {
  const getOptionLabel = (type?: LocalActionType) => actionOptions.find((o) => o.value === type)?.label || type

  const selectedValue = finalAction?.type || 'none'
  const renderIcon = (value: LocalActionType) => {
    const icon = actionOptions.find((o) => o.value === value)?.icon
    return typeof icon === 'string' ? <Icon>{icon}</Icon> : icon?.()
  }

  const handleSelect = (_: React.MouseEvent<HTMLElement, MouseEvent>, value: LocalActionType | 'none' | null) => {
    if (value === 'none') {
      onRemove()
      return
    }
    if (value === null) {
      // self deselect, open current final action for edit
      if (finalAction) {
        onEdit(finalAction.type)
      }
      return
    }
    onAdd(value)
  }

  return (
    <FooterRow>
      <Typography variant="body2" sx={{ flex: '0 0 140px' }}>
        After the Actions
      </Typography>
      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
        <ToggleButtonGroup
          data-testid="campaign-final-actions"
          exclusive
          color="primary"
          size="small"
          value={selectedValue}
          sx={{ flexWrap: 'wrap', minWidth: 0, width: '100%' }}
          onChange={handleSelect}
        >
          <ToggleButton
            value="none"
            color="primary"
            sx={{ textTransform: 'none', display: 'flex', flex: '0 0 auto', gap: 0.5, minWidth: 0 }}
          >
            <Icon>skip_next</Icon>
            <Typography variant="body2" component="span" noWrap>
              {continueLabel}
            </Typography>
          </ToggleButton>
          {actionOptions.map(({ value, color }) => {
            const label = getOptionLabel(value)
            const isSelected = selectedValue === value
            const showPreview = Boolean(isSelected && finalAction && hasPreview(finalAction))
            const canEdit = value && editableActions.indexOf(value as string) !== -1
            const buttonColor = color || 'primary'

            return (
              <ToggleButton
                key={value}
                value={value}
                color={buttonColor}
                sx={{
                  textTransform: 'none',
                  display: 'flex',
                  flex: '0 1 auto',
                  gap: 0.5,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {renderIcon(value)}
                <Typography variant="body2" component="span" noWrap sx={{ flex: '0 0 auto' }}>
                  {label}
                </Typography>
                {showPreview && finalAction && (
                  <FinalActionPreview
                    action={finalAction}
                    files={files}
                    personalizationList={personalizationList}
                    onEdit={canEdit ? () => onEdit(value) : undefined}
                  />
                )}
              </ToggleButton>
            )
          })}
        </ToggleButtonGroup>
      </Box>
    </FooterRow>
  )
}

export default FinalActionsRow

const FooterRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginTop: 16,
  minWidth: 0,
})
